import * as React from 'react';
import {
  Box,
  Table as MaterialTable,
  TableCell as MaterialTableCell,
  TableContainer as MaterialTableContainer,
  TableHead as MaterialTableHead,
  TableRow as MaterialTableRow
} from '@mui/material';

import { runtimeEnv } from 'environments/runtime-env';
import { Container, TableBody, TableFooter, TableHeader, TableHeaderCheckboxCell, ZeroState } from 'ui';
import palette from 'ui/theme/palette';
import { getComparator } from './util/get-comparator';

export type Order = 'asc' | 'desc';

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);

    if (order !== 0) {
      return order;
    }

    return a[1] - b[1];
  });

  return stabilizedThis.map((el) => el[0]);
}
export interface Column<T> {
  id: keyof T;
  label: string | React.ReactNode;
  format?: (val: T) => string | JSX.Element;
  disablePadding?: boolean;
  align?: 'left' | 'center' | 'right' | 'justify' | 'inherit' | undefined;
  widthEven?: boolean;
  widthPercent?: number;
  minWidth?: number;
  /**
   * Pins the column left in scroll mode (leading consecutive columns only; the checkbox pins
   * automatically). Renders at exactly `minWidth`. Not for expandable rows or `singleSelectable`.
   */
  pinned?: boolean;
  /** Allows the column to be hidden via `hiddenColumns`. Columns without it are always shown. */
  hideable?: boolean;
  sortFunction?: (a: Row<T>, b: Row<T>) => number;
  noSort?: boolean;
  tooltip?: string;
  disableRowTooltip?: boolean;
}

export interface PinnedCellLayout {
  left: number;
  isLast: boolean;
}

export interface PinnedLayout {
  checkbox: boolean;
  cells: { [columnId: string]: PinnedCellLayout };
}

/** Width of the row-select checkbox cell when it is pinned (horizontal scroll mode). */
export const PINNED_CHECKBOX_WIDTH = 50;

export interface Row<T> {
  id: string;
  data: T;
  selectTooltip?: string;
  heavyBottomBorder?: boolean;
  heavyTopBorder?: boolean;
  highlightBackground?: string;
  /**
   * Set to false if this specific row isn't clickable
   */
  clickable?: boolean;
  /**
   * Shows the row as disabled and unclickable
   */
  disabled?: boolean;
  /**
   * Shows the row with an archived background
   */
  archived?: boolean;
  /**
   * Set to false to hide the checkbox on selectable tables
   */
  selectable?: boolean;
  /**
   * Show checkbox but unselectable
   */
  disableSelect?: boolean;
  disabledTooltip?: string;
  expandedContent?: React.ReactNode;
}

export interface Props<T> {
  columns: Column<T>[];
  rows: Row<T>[];
  maxHeight?: string;
  defaultSort: keyof T;
  defaultSortDirection?: Order;
  noDataComponent?: React.FC<unknown>;
  noPagination?: boolean;
  clickable?: boolean;
  defaultRowCount?: number;
  updateSelected?: string[];
  updateSelectedRow?: string;
  returnSelection?: (selectedRows: string[]) => void;
  selectable?: boolean;
  defaultSelected?: string[];
  onClick?: (id: string, data: T) => void;
  dataCy?: string;
  actions?: React.ReactNode;
  singleSelectable?: boolean;
  tableLayout?: 'auto' | 'fixed' | 'initial' | 'inherit' | 'unset';
  showFilterZeroState?: boolean;
  minWidth?: string | number;
  horizontalScroll?: boolean;
  stickyHeader?: boolean;
  /** Column ids to hide. Only columns marked `hideable` are affected. */
  hiddenColumns?: string[];
  footerRows?: JSX.Element;
  page?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (pageSize: number) => void;
  onSort?: (sort: string, sortDirection: Order) => void;
  /** Keeps matching rows above the rest, whichever column and direction the table is sorted by. */
  pinRowsToTop?: (row: Row<T>) => boolean;
}

const FilterZeroState = () => {
  return (
    <ZeroState
      title={'There are no results to show with the filters.'}
      subTitle={'Please try changing your filters to show more records.'}
      icon="/img/empty-states/NoResults.svg"
      noBorder
    />
  );
};

export const Table = <T,>(props: Props<T>) => {
  const [order, setOrder] = React.useState<Order>(props.defaultSortDirection ?? 'desc');
  const [orderBy, setOrderBy] = React.useState<keyof T>(props.defaultSort);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [selectedRow, setSelectedRow] = React.useState('');
  const [page, setPage] = React.useState(props.page ? props.page : 0);
  const [rowsPerPage, setRowsPerPage] = React.useState(props.defaultRowCount ?? 25);

  React.useEffect(() => {
    if (props.updateSelected) {
      setSelected(props.updateSelected);
    }
  }, [props.updateSelected]);

  React.useEffect(() => {
    if (props.updateSelectedRow !== undefined) {
      setSelectedRow(props.updateSelectedRow);
      setSelected(props.updateSelectedRow.length === 0 ? [] : [props.updateSelectedRow]);
    }
  }, [props.updateSelectedRow]);

  React.useEffect(() => {
    if (rows.length > 0) {
      if (page * rowsPerPage >= rows.length) {
        setPage(0);
        props.onPageChange?.(0);
      }
    }
    if (props.noPagination) {
      setRowsPerPage(props.rows.length);
    }
  }, [props.rows.length]);

  React.useEffect(() => {
    if (props.defaultSelected) {
      setSelected(props.defaultSelected);
      props.returnSelection && props.returnSelection(props.defaultSelected);
    }
  }, []);

  React.useEffect(() => {
    if (props.defaultRowCount) {
      setRowsPerPage(props.defaultRowCount);
    }
  }, [props.defaultRowCount]);

  React.useEffect(() => {
    if (props.page !== undefined) {
      setPage(props.page);
    }
  }, [props.page]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof T) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    if (props.onSort) {
      props.onSort(property as string, isAsc ? 'desc' : 'asc');
    }
  };

  const handleRequestSortBySelected = (event: React.MouseEvent<unknown>) => {
    const isAsc = orderBy === 'selected' && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy('selected' as keyof T);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && selected.length === 0) {
      const newSelecteds = props.rows
        .filter((row) => !row.disableSelect && row.selectable !== false)
        .map((n) => n.id);
      setSelected(newSelecteds);
      props.returnSelection && props.returnSelection(newSelecteds);
      return;
    }

    setSelected([]);
    props.returnSelection && props.returnSelection([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string, data: T) => {
    event.stopPropagation();
    if (props.clickable && props.onClick) {
      props.onClick(id, data);
    }
  };

  const handleSelect = (event: React.MouseEvent<unknown>, id: string) => {
    if (props.selectable) {
      const selectedIndex = selected.indexOf(id);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
      }

      props.returnSelection && props.returnSelection(newSelected);
      setSelected(newSelected);
    }
  };

  const handleRadio = (value: string) => {
    if (props.singleSelectable) {
      props.returnSelection && props.returnSelection([value]);
      setSelected([value]);
      setSelectedRow(value);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    if (props.onPageChange) {
      props.onPageChange(newPage);
    }
  };

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setPage(0);

    if (props.onRowsPerPageChange) {
      props.onRowsPerPageChange(value);
    }
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  React.useEffect(() => {
    if (runtimeEnv.NODE_ENV !== 'production' && props.horizontalScroll) {
      const percentColumns = props.columns.filter((c) => c.widthPercent || c.widthEven);
      if (percentColumns.length > 0) {
        console.warn(
          `Table: widthPercent/widthEven are ignored when horizontalScroll is set — use minWidth instead (columns: ${percentColumns
            .map((c) => String(c.id))
            .join(', ')})`
        );
      }
      const missingMinWidth = props.columns.filter((c) => !c.minWidth);
      if (missingMinWidth.length > 0) {
        console.warn(
          `Table: columns should define minWidth when horizontalScroll is set — they will act as flex columns (columns: ${missingMinWidth
            .map((c) => String(c.id))
            .join(', ')})`
        );
      }
    }
    if (runtimeEnv.NODE_ENV !== 'production' && props.columns.some((c) => c.pinned)) {
      if (!props.horizontalScroll) {
        console.warn('Table: pinned columns require horizontalScroll — pinning is ignored');
      }
      const firstUnpinned = props.columns.findIndex((c) => !c.pinned);
      const strayPinned =
        firstUnpinned === -1 ? [] : props.columns.slice(firstUnpinned).filter((c) => c.pinned);
      if (strayPinned.length > 0) {
        console.warn(
          `Table: pinned is only supported on leading consecutive columns — ignoring: ${strayPinned
            .map((c) => String(c.id))
            .join(', ')}`
        );
      }
    }
  }, []);

  const allColumns = props.horizontalScroll
    ? props.columns.map((c) => ({ ...c, widthPercent: undefined, widthEven: false }))
    : props.columns;
  // Filter before the pinned layout is computed so sticky offsets reflect what actually renders.
  const columns = props.hiddenColumns?.length
    ? allColumns.filter((c) => !(c.hideable && props.hiddenColumns?.includes(String(c.id))))
    : allColumns;

  let pinnedLayout: PinnedLayout | undefined;
  if (props.horizontalScroll) {
    const leadingPinned: Column<T>[] = [];
    for (const column of columns) {
      if (!column.pinned) {
        break;
      }
      leadingPinned.push(column);
    }
    // The row-select checkbox pins even when no data column does, so it never scrolls away.
    if (leadingPinned.length > 0 || props.selectable) {
      const cells: PinnedLayout['cells'] = {};
      let left = props.selectable ? PINNED_CHECKBOX_WIDTH : 0;
      leadingPinned.forEach((column, index) => {
        cells[String(column.id)] = { left, isLast: index === leadingPinned.length - 1 };
        left += column.minWidth ?? 0;
      });
      pinnedLayout = { checkbox: Boolean(props.selectable), cells };
    }
  }

  // Scroll mode: <colgroup> + fixed layout render each column at minWidth (stable across
  // sort/filter/pagination); the last unpinned column stays width-less and absorbs surplus width.
  const flexColumnId = props.horizontalScroll
    ? [...columns].reverse().find((c) => !c.pinned && c.minWidth)?.id
    : undefined;
  const totalConfiguredWidth =
    (props.selectable ? PINNED_CHECKBOX_WIDTH : 0) +
    columns.reduce((total, column) => total + (column.minWidth ?? 0), 0);

  let rows = props.rows;
  const sortByColumn = props.columns.find((c) => c.id === orderBy);
  if (sortByColumn && sortByColumn.sortFunction) {
    rows = stableSort(props.rows, sortByColumn.sortFunction);
    rows = order === 'asc' ? rows : rows.reverse();
  } else {
    rows = stableSort(props.rows, getComparator(order, orderBy));
  }

  const pinRowsToTop = props.pinRowsToTop;

  if (pinRowsToTop) {
    rows = [...rows.filter((row) => pinRowsToTop(row)), ...rows.filter((row) => !pinRowsToTop(row))];
  }
  const rowsSortedBySelected =
    orderBy === 'selected'
      ? rows.sort((r: Row<T>) => (order === 'asc' ? (isSelected(r.id) ? 1 : -1) : isSelected(r.id) ? -1 : 1))
      : rows;
  const hasExpandableRows = rowsSortedBySelected.some((row) => Boolean(row.expandedContent));
  const ZeroState = props.noDataComponent;

  if (props.showFilterZeroState) {
    return <FilterZeroState />;
  } else {
    return (
      <div data-cy={props.dataCy}>
        {props.rows.length < 1 && ZeroState ? (
          <ZeroState />
        ) : (
          <>
            {selected.length > 0 && props.actions && !props.horizontalScroll && (
              <Box
                sx={{
                  width: '100%',
                  height: '40px',
                  bgcolor: 'background.secondary',
                  borderBottom: (theme) => `0.016rem solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <TableHeaderCheckboxCell
                  selected={selected}
                  rows={props.rows}
                  orderBy={orderBy}
                  order={order}
                  handleSelectAllClick={handleSelectAllClick}
                  handleSort={handleRequestSortBySelected}
                  sx={{ borderBottom: 'none' }}
                />
                <Container padding="0 0 0 18px">{props.actions}</Container>
              </Box>
            )}
            <MaterialTableContainer
              sx={{
                maxHeight: props.maxHeight ? props.maxHeight : undefined,
                overflowY: 'auto',
                overflowX: props.horizontalScroll ? 'auto' : undefined
              }}
            >
              <MaterialTable
                stickyHeader={props.stickyHeader}
                sx={{
                  height: props.maxHeight ? 'max-content' : undefined,
                  tableLayout: props.horizontalScroll ? 'fixed' : props.tableLayout ?? 'fixed',
                  // Column total triggers scroll instead of squish; the flex column absorbs surplus.
                  minWidth: props.horizontalScroll
                    ? `${totalConfiguredWidth}px`
                    : props.minWidth
                    ? props.minWidth
                    : '750px',
                  width: '100%',
                  // Header paints above pinned body cells; the compound selector puts pinned corners on top.
                  '& .MuiTableCell-stickyHeader': {
                    bgcolor: 'background.secondary',
                    zIndex: 4
                  },
                  '& .MuiTableCell-stickyHeader.gcv-table-cell--pinned': {
                    zIndex: 7
                  }
                }}
              >
                {props.horizontalScroll && (
                  <colgroup>
                    {props.selectable && <col style={{ width: `${PINNED_CHECKBOX_WIDTH}px` }} />}
                    {columns.map((column) => (
                      <col
                        key={String(column.id)}
                        style={
                          column.minWidth && column.id !== flexColumnId
                            ? { width: `${column.minWidth}px` }
                            : undefined
                        }
                      />
                    ))}
                  </colgroup>
                )}
                {selected.length > 0 && props.actions && props.horizontalScroll ? (
                  // Bulk actions render as a sticky header row; sticky-left keeps them visible mid-scroll.
                  <MaterialTableHead>
                    <MaterialTableRow data-cy="table-bulk-actions-row">
                      {props.selectable && (
                        <TableHeaderCheckboxCell
                          selected={selected}
                          rows={props.rows}
                          orderBy={orderBy}
                          order={order}
                          handleSelectAllClick={handleSelectAllClick}
                          handleSort={handleRequestSortBySelected}
                          pinned={pinnedLayout?.checkbox}
                        />
                      )}
                      <MaterialTableCell
                        colSpan={columns.length}
                        padding="none"
                        sx={{ height: '40px', p: 0 }}
                      >
                        <Box
                          sx={{
                            position: 'sticky',
                            left: pinnedLayout?.checkbox ? `${PINNED_CHECKBOX_WIDTH}px` : 0,
                            width: 'fit-content',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Container padding="0 0 0 18px">{props.actions}</Container>
                        </Box>
                      </MaterialTableCell>
                    </MaterialTableRow>
                  </MaterialTableHead>
                ) : selected.length && props.actions ? null : (
                  <TableHeader
                    selectable={props.selectable || false}
                    singleSelectable={props.singleSelectable || false}
                    columns={columns}
                    pinnedLayout={pinnedLayout}
                    selectedRows={selected}
                    rows={rowsSortedBySelected}
                    orderBy={orderBy}
                    order={order}
                    handleSelectAllClick={handleSelectAllClick}
                    handleRequestSort={handleRequestSort}
                    handleRequestSortBySelected={handleRequestSortBySelected}
                    hasExpandableRows={hasExpandableRows}
                  />
                )}
                <TableBody
                  rows={rowsSortedBySelected}
                  selectable={props.selectable || false}
                  singleSelectable={props.singleSelectable || false}
                  clickable={props.clickable || false}
                  columns={columns}
                  pinnedLayout={pinnedLayout}
                  handleSelect={handleSelect}
                  handleClick={handleClick}
                  handleRadio={handleRadio}
                  selected={selected}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  selectedRow={selectedRow}
                  hasExpandableRows={hasExpandableRows}
                ></TableBody>
              </MaterialTable>
            </MaterialTableContainer>
            {!props.noPagination ? (
              <TableFooter
                minWidth={props.minWidth}
                rows={props.rows}
                footerRows={props.footerRows}
                defaultRowCount={props.rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                handlePageChange={handlePageChange}
                handleRowsPerPageChange={handleRowsPerPageChange}
              />
            ) : props.footerRows ? (
              <Box
                sx={{
                  backgroundColor: palette.background.secondary,
                  borderTop: `1px solid ${palette.divider}`
                }}
              >
                {props.footerRows}
              </Box>
            ) : null}
          </>
        )}
      </div>
    );
  }
};
